import { Delete, Edit, FilterList, Search } from "@mui/icons-material";
import {
    Avatar,
    Box,
    Chip,
    CircularProgress,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import React, { useContext, useMemo, useState } from "react";
import { AppContext } from "../../../AppContext";
import ConfirmationDialog from "../../../components/ConfirmationDialog";
import config from "../../../config";
import { uploadFileOnFileSystem } from "../../../shared/hooks/useCreateResources";
import {
    useDeleteDoctype,
    useFrappeToken,
    useLoadDoctypeMitation,
    useSaveDoc,
} from "../../../shared/hooks/useFrappeDoctype";

const PurchaseItemsTab = ({
    items,
    itemGroups,
    onEdit,
    refreshTrigger,
    refetchItems,
}) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [uploadingImage, setUploadingImage] = useState(null);
    const [sortBy, setSortBy] = useState("item_code");
    const [sortOrder, setSortOrder] = useState("asc");
    const [filterItemGroup, setFilterItemGroup] = useState("");
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const { company } = useContext(AppContext);

    const deleteItem = useDeleteDoctype();
    const { data: frappeToken } = useFrappeToken(true);
    const loadDoctype = useLoadDoctypeMitation();
    const saveDoc = useSaveDoc();

    const filteredAndSortedItems = useMemo(() => {
        let filtered = items.filter((item) => {
            // Search filter
            const matchesSearch =
                item.item_code
                    ?.toLowerCase()
                    .includes(searchTerm.toLowerCase()) ||
                item.item_name
                    ?.toLowerCase()
                    .includes(searchTerm.toLowerCase()) ||
                item.item_group
                    ?.toLowerCase()
                    .includes(searchTerm.toLowerCase());

            // Item group filter
            const matchesGroup =
                filterItemGroup === "" || item.item_group === filterItemGroup;

            return matchesSearch && matchesGroup;
        });

        // Sort the filtered items
        filtered.sort((a, b) => {
            let aValue = a[sortBy] || "";
            let bValue = b[sortBy] || "";

            // Convert to string for comparison
            aValue = aValue.toString().toLowerCase();
            bValue = bValue.toString().toLowerCase();

            if (sortOrder === "asc") {
                return aValue.localeCompare(bValue);
            } else {
                return bValue.localeCompare(aValue);
            }
        });

        return filtered;
    }, [items, searchTerm, filterItemGroup, sortBy, sortOrder]);

    const handleSort = (column) => {
        if (sortBy === column) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortBy(column);
            setSortOrder("asc");
        }
    };

    const handleDelete = (item) => {
        setItemToDelete(item);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (itemToDelete) {
            setIsDeleting(true);
            try {
                await deleteItem.mutateAsync({
                    doctype: "Item",
                    name: itemToDelete.item_code,
                });
                enqueueSnackbar("Purchase item deleted successfully", {
                    variant: "success",
                });
                // Refetch local items
                if (refetchItems) {
                    refetchItems();
                }
            } catch (error) {
                enqueueSnackbar("Failed to delete purchase item", {
                    variant: "error",
                });
            } finally {
                setIsDeleting(false);
            }
        }
        setDeleteDialogOpen(false);
        setItemToDelete(null);
    };

    const handleCancelDelete = () => {
        setDeleteDialogOpen(false);
        setItemToDelete(null);
    };

    const handleAvatarClick = (item) => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "image/*";
        input.onchange = async (e) => {
            const file = e.target.files[0];
            if (file) {
                setUploadingImage(item.item_code);
                try {
                    // Step 1: Upload file using uploadFileOnFileSystem
                    const uploadResponse = await uploadFileOnFileSystem(
                        file,
                        null, // onUploadProgress
                        item.item_code, // docname
                        "Item", // doctype
                        "image", // fieldname
                        frappeToken // csrfToken
                    );

                    const uploadResult = await uploadResponse.json();

                    if (uploadResult.message && uploadResult.message.file_url) {
                        // Step 2: Fetch the latest document from database
                        const latestDocResponse = await loadDoctype.mutateAsync(
                            {
                                doctype: "Item",
                                name: item.item_code,
                            }
                        );

                        const latestDoc = latestDocResponse.data.docs[0];

                        // Step 3: Update the latest document with new image URL
                        const updatedItem = {
                            ...latestDoc,
                            image: uploadResult.message.file_url,
                            __unsaved: 1,
                        };

                        await saveDoc.mutateAsync({
                            payload: updatedItem,
                            action: "Save",
                        });

                        enqueueSnackbar("Image uploaded successfully", {
                            variant: "success",
                        });

                        // Refetch items to show updated image
                        if (refetchItems) {
                            refetchItems();
                        }
                    } else {
                        throw new Error("Invalid upload response");
                    }
                } catch (error) {
                    console.error("Upload error:", error);
                    enqueueSnackbar("Failed to upload image", {
                        variant: "error",
                    });
                } finally {
                    setUploadingImage(null);
                }
            }
        };
        input.click();
    };

    return (
        <Box>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
                flexWrap="wrap"
                gap={2}
            >
                <Typography variant="h6">Purchase Items</Typography>
                <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Filter by Group</InputLabel>
                        <Select
                            value={filterItemGroup}
                            onChange={(e) => setFilterItemGroup(e.target.value)}
                            label="Filter by Group"
                            startAdornment={<FilterList sx={{ mr: 1 }} />}
                        >
                            <MenuItem value="">All Groups</MenuItem>
                            {itemGroups.map((group) => (
                                <MenuItem
                                    key={group.item_group_name}
                                    value={group.item_group_name}
                                >
                                    {group.item_group_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        size="small"
                        placeholder="Search purchase items..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ width: 300 }}
                    />
                </Box>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Image</TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={sortBy === "item_code"}
                                    direction={
                                        sortBy === "item_code"
                                            ? sortOrder
                                            : "asc"
                                    }
                                    onClick={() => handleSort("item_code")}
                                >
                                    Item Code
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={sortBy === "item_name"}
                                    direction={
                                        sortBy === "item_name"
                                            ? sortOrder
                                            : "asc"
                                    }
                                    onClick={() => handleSort("item_name")}
                                >
                                    Item Name
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={sortBy === "item_group"}
                                    direction={
                                        sortBy === "item_group"
                                            ? sortOrder
                                            : "asc"
                                    }
                                    onClick={() => handleSort("item_group")}
                                >
                                    Item Group
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={sortBy === "valuation_rate"}
                                    direction={
                                        sortBy === "valuation_rate"
                                            ? sortOrder
                                            : "asc"
                                    }
                                    onClick={() => handleSort("valuation_rate")}
                                >
                                    Valuation Rate (Buying Price)
                                </TableSortLabel>
                            </TableCell>

                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredAndSortedItems.map((item) => (
                            <TableRow key={item.item_code} hover>
                                <TableCell>
                                    <Box position="relative">
                                        <Avatar
                                            src={
                                                item.image
                                                    ? `${config.baseUrl}${item.image}`
                                                    : undefined
                                            }
                                            alt={item.item_name}
                                            onClick={() =>
                                                uploadingImage !==
                                                    item.item_code &&
                                                handleAvatarClick(item)
                                            }
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                cursor:
                                                    uploadingImage ===
                                                    item.item_code
                                                        ? "default"
                                                        : "pointer",
                                                "&:hover": uploadingImage !==
                                                    item.item_code && {
                                                    opacity: 0.8,
                                                    transform: "scale(1.05)",
                                                },
                                                transition:
                                                    "all 0.2s ease-in-out",
                                                opacity:
                                                    uploadingImage ===
                                                    item.item_code
                                                        ? 0.7
                                                        : 1,
                                            }}
                                        >
                                            {item.item_name
                                                ?.charAt(0)
                                                .toUpperCase()}
                                        </Avatar>
                                        {uploadingImage === item.item_code && (
                                            <CircularProgress
                                                size={20}
                                                sx={{
                                                    position: "absolute",
                                                    top: "50%",
                                                    left: "50%",
                                                    marginTop: "-10px",
                                                    marginLeft: "-10px",
                                                    color: "primary.main",
                                                }}
                                            />
                                        )}
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Typography
                                        variant="body2"
                                        fontWeight="bold"
                                    >
                                        {item.item_code}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">
                                        {item.item_name}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={item.item_group}
                                        size="small"
                                        variant="outlined"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Box display="flex" alignItems="center">
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: "bold",
                                                marginRight: 0.5,
                                            }}
                                        >
                                            {company?.symbol || "$"}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            fontWeight="bold"
                                            color="primary"
                                        >
                                            {item.valuation_rate || 0}
                                        </Typography>
                                    </Box>
                                </TableCell>

                                <TableCell>
                                    <Box display="flex" gap={1}>
                                        <Tooltip title="Edit">
                                            <IconButton
                                                onClick={() => onEdit(item)}
                                                color="primary"
                                                size="small"
                                            >
                                                <Edit />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton
                                                onClick={() =>
                                                    handleDelete(item)
                                                }
                                                color="error"
                                                size="small"
                                            >
                                                <Delete />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                        {filteredAndSortedItems.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        No purchase items found
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <ConfirmationDialog
                open={deleteDialogOpen}
                onConfirm={handleConfirmDelete}
                onClose={handleCancelDelete}
                title="Delete Purchase Item"
                message={`Are you sure you want to delete "${itemToDelete?.item_code}"? This action cannot be undone.`}
                confirmText="Delete"
                confirmColor="error"
                cancelText="Cancel"
                loading={isDeleting}
            />
        </Box>
    );
};

export default PurchaseItemsTab;
